/**
 * FairwayBid – Struktur der Angebote
 *
 * Ein Angebot war bisher ein Preis plus Freitext. Damit ist kein Vergleich
 * möglich: Der eine schreibt die Montage in den Preis, der andere erwähnt sie
 * nicht, der dritte nennt eine Wartung pro Jahr. Der Club müsste
 * zurückfragen – und genau das soll FairwayBid ihm ersparen.
 *
 * Deshalb beantworten alle Händler dieselben Fragen. Sharetribe nennt das
 * "transaction fields": Sie hängen am Listing-Typ, erscheinen automatisch im
 * Angebotsformular und landen in den geschützten Daten der Transaktion – dort
 * lesen wir sie für die Vergleichstabelle wieder aus.
 *
 * Die Felder stehen hier im Code und nicht in der Console, weil sie die
 * Vergleichstabelle definieren: Wer ein Feld entfernt, bricht den Vergleich.
 * Sobald der Satz steht, können sie ohne Codeänderung in die Console wandern –
 * dann gewinnt die Console, weil wir nur ergänzen, wenn dort nichts gepflegt ist.
 */

const opt = (option, label) => ({ option, label });

// Die Angebotsfelder fuellt der Haendler aus. Sharetribe legt sie deshalb
// unter dem Praefix "provider_" in den geschuetzten Daten der Transaktion ab.
export const OFFER_ROLE = 'provider';
export const OFFER_DATA_PREFIX = `${OFFER_ROLE}_`;

export const OFFER_FIELDS = [
  {
    key: 'offer_unit_price',
    showTo: OFFER_ROLE,
    label: 'Einzelpreis netto',
    schemaType: 'shortText',
    saveConfig: {
      required: true,
      placeholderMessage: 'z. B. 0,38 € je Ball oder 12.400 € je Gerät',
    },
  },
  {
    key: 'offer_delivery_weeks',
    showTo: OFFER_ROLE,
    label: 'Lieferzeit ab Auftrag',
    schemaType: 'enum',
    enumOptions: [
      opt('dw_1_2', '1 bis 2 Wochen'),
      opt('dw_3_4', '3 bis 4 Wochen'),
      opt('dw_5_8', '5 bis 8 Wochen'),
      opt('dw_over_8', 'über 8 Wochen'),
    ],
    saveConfig: { required: true },
  },
  {
    key: 'offer_warranty',
    showTo: OFFER_ROLE,
    label: 'Garantie',
    schemaType: 'enum',
    enumOptions: [
      opt('wr_12', '12 Monate'),
      opt('wr_24', '24 Monate'),
      opt('wr_36', '36 Monate'),
      opt('wr_60', '60 Monate'),
      opt('wr_none', 'keine über die Gewährleistung hinaus'),
    ],
    saveConfig: { required: true },
  },
  {
    key: 'offer_installation',
    showTo: OFFER_ROLE,
    label: 'Montage und Inbetriebnahme',
    schemaType: 'enum',
    enumOptions: [
      opt('in_included', 'im Preis enthalten'),
      opt('in_extra', 'gegen Aufpreis'),
      opt('in_none', 'nicht angeboten'),
      opt('in_na', 'nicht erforderlich'),
    ],
    saveConfig: { required: true },
  },
  {
    key: 'offer_validity',
    showTo: OFFER_ROLE,
    label: 'Angebot gültig',
    schemaType: 'enum',
    enumOptions: [
      opt('v_14', '14 Tage'),
      opt('v_30', '30 Tage'),
      opt('v_60', '60 Tage'),
      opt('v_season', 'bis Saisonende'),
    ],
    saveConfig: { required: true },
  },
  {
    key: 'offer_maintenance',
    showTo: OFFER_ROLE,
    label: 'Wartung pro Jahr, netto',
    schemaType: 'shortText',
    saveConfig: { required: false, placeholderMessage: 'optional, z. B. 640 € inkl. zwei Terminen' },
  },
  {
    key: 'offer_tradein',
    showTo: OFFER_ROLE,
    label: 'Rücknahme Altgerät',
    schemaType: 'enum',
    enumOptions: [
      opt('td_credit', 'ja, mit Anrechnung auf den Preis'),
      opt('td_yes', 'ja, kostenfreie Entsorgung'),
      opt('td_no', 'nein'),
    ],
    saveConfig: { required: false },
  },
];

/**
 * Ergänzt die Angebotsfelder an jedem Listing-Typ, der in der Console keine
 * eigenen gepflegt hat. Console schlägt Code – wer dort Felder anlegt, gewinnt.
 */
export const withOfferFields = (listingTypes = []) =>
  (Array.isArray(listingTypes) ? listingTypes : []).map(listingType =>
    listingType?.transactionFields?.length > 0
      ? listingType
      : { ...listingType, transactionFields: OFFER_FIELDS }
  );

const LABELS = OFFER_FIELDS.reduce((acc, f) => ({ ...acc, [f.key]: f.label }), {});

const OPTION_LABELS = OFFER_FIELDS.reduce((acc, f) => {
  (f.enumOptions || []).forEach(o => {
    acc[`${f.key}:${o.option}`] = o.label;
  });
  return acc;
}, {});

export const getOfferFieldLabel = key => LABELS[key] || key;

export const getOfferFieldValue = (key, raw) => {
  if (raw === null || raw === undefined || raw === '') return null;
  return OPTION_LABELS[`${key}:${raw}`] || String(raw);
};

/**
 * Die Angaben eines Angebots in fester Reihenfolge – so bleiben die Zeilen der
 * Vergleichstabelle bei allen Angeboten gleich, auch wenn einer ein
 * Wahlfeld leer gelassen hat.
 */
export const getOfferFieldRaw = (transaction, key) => {
  const data = transaction?.attributes?.protectedData || {};
  // Erst der Weg, den Sharetribe nutzt, dann der ohne Praefix - so lesen wir
  // auch Angebote, die spaeter ueber Console-Felder ohne Rolle entstehen.
  const prefixed = data[`${OFFER_DATA_PREFIX}${key}`];
  return prefixed !== undefined ? prefixed : data[key];
};

export const getOfferDetails = transaction =>
  OFFER_FIELDS.map(f => ({
    key: f.key,
    label: f.label,
    value: getOfferFieldValue(f.key, getOfferFieldRaw(transaction, f.key)),
  }));
